import { useEffect, useReducer, useState } from "react";
import { flow } from "lodash";

import { TimesheetEntry, ExportedTimesheetEntry } from "../core/types";
import timesheetReducer, {
  timesheetActions,
  timesheetSelectors
} from "../core/timesheet";
import StorageApi from "../core/storage";
import useWindowFocus from "./use-focus";

/**
 * Provides a hook to a simple (storage persisted) CRUD interface for timesheet entries
 */
function useTimesheet(sheetName?: string) {
  const url = new URL(window.location.href);
  const sheetParam = url.searchParams.get("sheet");

  const [state, dispatch] = useReducer(
    timesheetReducer,
    sheetName,
    StorageApi.loadState
  );

  const [isInitialized, setIsInitialized] = useState(false);

  // initialize: change the sheet based on the ?sheet query param
  useEffect(() => {
    // do this only once
    if (isInitialized) {
      return;
    }

    try {
      // no parameter/sheet is already active?
      if (
        sheetParam === null ||
        timesheetSelectors.getActiveSheet(state) === sheetParam
      ) {
        return;
      }

      // remove the param, when the sheet does not exist
      if (!state.sheets.includes(sheetParam)) {
        url.searchParams.delete("sheet");
        history.replaceState(
          { sheetName: state.sheets[0] },
          state.sheets[0],
          url.href
        );
        return;
      }

      // change the sheet
      dispatch(
        timesheetActions.changeSheet({
          entries: StorageApi.loadEntries(sheetParam),
          sheetName: sheetParam
        })
      );
    } finally {
      setIsInitialized(true);
    }
  }, [state, sheetParam, isInitialized]);

  const [hasStorageChanges, setHasStorageChanges] = useState(false);

  useEffect(() => {
    StorageApi.saveState(state);
  }, [state]);

  const activeSheet = timesheetSelectors.getActiveSheet(state);
  const hasFocus = useWindowFocus();

  /**
   * Reloads the state, when a the local storage has been modified in the background
   */
  useEffect(() => {
    if (hasFocus && hasStorageChanges) {
      dispatch(timesheetActions.load(StorageApi.loadState()));
      setHasStorageChanges(false);
    }

    if (!hasFocus) {
      return StorageApi.sync(activeSheet, () => setHasStorageChanges(true));
    }
  }, [activeSheet, hasFocus, hasStorageChanges]);

  function pushLocation(sheetName = timesheetSelectors.getActiveSheet(state)) {
    try {
      const url = new URL(window.location.href);

      url.searchParams.set("sheet", sheetName);

      history.pushState(
        {
          sheetName: timesheetSelectors.getActiveSheet(state)
        },
        sheetName,
        url.href
      );
    } catch (err) {
      console.warn("couldn't change location: %s", err.message);
    }
  }

  return {
    entries: state.entries,
    error: state.error,
    activeSheet: timesheetSelectors.getActiveSheet(state),
    sheets: state.sheets,
    createEntry: flow(timesheetActions.create, dispatch),
    latestImport: state.latestImport,
    updateEntry(
      entry: Pick<TimesheetEntry, "id">,
      updatedEntry: Pick<TimesheetEntry, "description" | "duration" | "start">
    ) {
      dispatch(timesheetActions.update({ entry, updatedEntry }));
    },
    removeEntry: flow(timesheetActions.remove, dispatch),
    import(
      entries: ExportedTimesheetEntry[],
      options: { newSheet: boolean; sheetName?: string }
    ) {
      dispatch(timesheetActions.import({ entries, ...options }));

      if (options.newSheet) {
        pushLocation();
      }
    },
    createSheet(sheetName?: string) {
      dispatch(timesheetActions.createSheet({ sheetName }));
    },
    changeSheet(sheetName: string) {
      const entries = StorageApi.loadEntries(sheetName);
      dispatch(timesheetActions.changeSheet({ sheetName, entries }));

      pushLocation(sheetName);
    },
    removeSheet(sheetName: string) {
      dispatch(timesheetActions.removeSheet({ sheetName }));
      StorageApi.removeEntries(sheetName);
    },
    renameSheet(sheetName: string) {
      StorageApi.removeEntries(timesheetSelectors.getActiveSheet(state));
      dispatch(timesheetActions.renameSheet(sheetName));
    }
  };
}

export default useTimesheet;
